// The session's OS telemetry, for the band under the lanes.
//
// The band renders ON THE TURN AXIS: the conversation model's per-message
// timestamps (traceTurnTimes) date each turn well enough to bucket events
// under the turn whose work produced them, so the telemetry lines up with the
// lanes, the token strip, and the ruler instead of carrying a second axis.
// The dating is approximate and the band's label says how much of it is
// measured; a session that cannot be dated at all gets no band rather than a
// fabricated alignment.

import { matchCommand, matchPath, shellCommandCandidates } from "./traceEndpointMatch.js";
import { shellToolCommand } from "./shellToolCall.js";

// Kind groups, in band row order. DnsQuery rides with NetworkConnection: they
// answer the same reader question ("did it talk to the network"), and a
// four-row band costs vertical space the lanes need more.
export const SYSTEM_STREAM_KINDS = [
  { key: "process", label: "Processes", types: ["ProcessStart"] },
  { key: "file", label: "Files", types: ["FileState", "FileDelete", "FileRename"] },
  { key: "net", label: "Network", types: ["NetworkConnection", "DnsQuery"] },
];

const TYPE_TO_KIND = new Map(
  SYSTEM_STREAM_KINDS.flatMap((k) => k.types.map((t) => [t, k.key])),
);

export function systemKindOf(eventType) {
  return TYPE_TO_KIND.get(String(eventType || "")) ?? null;
}

// How far the band reads. The span floor is the first dated turn less a
// LEAD, clamped so a days-long session cannot demand a days-long scan; the
// ceiling extends a short TAIL past the focal capture because the response
// happens AFTER the request — the work the newly-captured response describes
// is exactly what lands in the tail.
//
// The lead exists because a session's processes are dated at their own start,
// which can precede the first prompt by minutes or hours (the tool is opened,
// then typed into). bucketEventsByTurn counts that lead-in on the first dated
// turn. The read is scoped to the session, so the lead adds only that
// session's pre-prompt rows. It reaches as far as the max span allows, so the
// whole read never exceeds SYSTEM_SPAN_MAX_MS + SYSTEM_TAIL_MS. `clamped`
// reports a session whose turns alone exceed the max span, not a lead cut
// short by it.
export const SYSTEM_SPAN_MAX_MS = 24 * 3600 * 1000;
export const SYSTEM_TAIL_MS = 15 * 60 * 1000;
export const SYSTEM_LEAD_MS = SYSTEM_SPAN_MAX_MS;

export function systemSpan({ focalTs, floorTs = null, leadMs = SYSTEM_LEAD_MS } = {}) {
  if (!Number.isFinite(focalTs)) return null;
  const turnsFloor = Number.isFinite(floorTs) ? floorTs : focalTs;
  const wantFloor = turnsFloor - Math.max(0, leadMs || 0);
  const maxFloor = focalTs - SYSTEM_SPAN_MAX_MS;
  return {
    fromTs: Math.max(wantFloor, maxFloor),
    toTs: focalTs + SYSTEM_TAIL_MS,
    clamped: turnsFloor < maxFloor,
  };
}

// Bar height for a count, in [0,1] against the band's max bucket. Log-scaled:
// system telemetry is bursty (one npm install is thousands of FileStates), and
// a linear scale would flatten everything that is not the burst.
export function bucketWeight(count, max) {
  if (!count || !max) return 0;
  return Math.log1p(count) / Math.log1p(max);
}

// --- Event ↔ turn correspondence --------------------------------------------
//
// The band buckets events by TIME; a name match is the stronger claim — this
// ProcessStart's command line IS the shell command a tool call in the slice
// ran. traceEndpointMatch owns the comparison rules (verbatim-or-program+arg
// for commands, two-segment floors for paths); this only builds its
// candidates from the slice's turns and reports WHICH turn matched, because
// the band's reading links back to the conversation.

export function sliceTurnCandidates(turns = [], start = 0, end = -1) {
  const out = [];
  const n = (turns || []).length;
  for (let t = Math.max(0, start); t <= end && t < n; t += 1) {
    const commands = [];
    const paths = [];
    for (const b of turns[t]?.blocks || []) {
      if (b?.type !== "tool_use") continue;
      const command = shellToolCommand(b.name, b.input);
      if (command) commands.push(...shellCommandCandidates(command));
      // parseApiRequest attaches per-block facets with file paths under
      // `files` (Set→Array converted) — there is no `paths` facet.
      for (const p of b.facets?.files || []) {
        if (typeof p === "string" && p.trim()) paths.push(p);
      }
    }
    if (commands.length || paths.length) out.push({ turn: t, commands, paths });
  }
  return out;
}

// Candidate ownership follows each event's approximate turn, not the rendered
// bucket that happened to fetch it. The latter changes with panel width and
// zoom, which must never change attribution. An event can only match a named
// call at or before its own position on the turn clock; exact command/path
// comparison still decides whether any of those prior calls correspond.
export function systemEventTurnCandidates(candidates = [], clockTimes = null, eventTs = null) {
  if (!Array.isArray(clockTimes) || !clockTimes.length) return candidates;
  if (!Number.isFinite(eventTs) || clockTimes.some((time) => !Number.isFinite(time))) return [];
  if (eventTs < clockTimes[0]) return [];
  let lo = 0;
  let hi = clockTimes.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (clockTimes[mid] >= eventTs) hi = mid;
    else lo = mid + 1;
  }
  let eventTurn = clockTimes[lo] >= eventTs ? lo : clockTimes.length - 1;
  // buildTurnTimes deliberately clamps undated head/tail turns to one anchor.
  // Every turn on that equal-time plateau is a possible owner; command/path
  // uniqueness below is the evidence that decides among them.
  while (eventTurn + 1 < clockTimes.length && clockTimes[eventTurn + 1] === clockTimes[eventTurn]) eventTurn += 1;
  return candidates.filter((candidate) => candidate.turn <= eventTurn);
}

// The agent harness wraps the tool's command before the OS sees it:
//   zsh -c source <snapshot> … && eval 'ACTUAL COMMAND' < /dev/null && pwd -P >| /tmp/…
// so the wrapper row — the FIRST process of every shell call, and often the
// only one a fast command leaves — never ends with the tool's command and
// never shares its program. Unwrapping the eval recovers the command the
// tool actually ran; the conservative comparison rules then apply to it
// unchanged. Returns null for anything that is not harness-shaped.
export function unwrapHarnessCommand(line) {
  const text = String(line || "");
  const i = text.indexOf("eval '");
  if (i < 0) return null;
  const j = text.indexOf("' < /dev/null", i);
  if (j <= i) return null;
  return text.slice(i + 6, j).replace(/'\\''/g, "'");
}

// A process key, normalized for joining. The keys are 56-bit integers, which
// a JS double CANNOT hold exactly — and the two places a key arrives from (a
// projected column, a parsed event_data payload) may round differently at the
// wire. Forcing both through the same Number() rounding before stringifying
// makes the spellings agree; the residual risk is two DISTINCT processes
// whose keys sit within the same rounding step, which for a seconds-wide
// slice of one machine is a mis-nested row, not a wrong claim.
export function pskOf(v) {
  if (v == null || v === "" || v === 0 || v === "0") return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? String(n) : null;
}

// The slice as a PROCESS TREE. ProcessStart events carry their own key plus
// their parent's (or creator's, for indirect spawns), and every other event
// carries the key of the process that did it — so the flat chronological list
// the dock used to show is recoverable structure: a tool call's wrapper
// shell, its pipeline stages as children, and the file activity of each
// process nested under it.
//
// Matching rides the tree: a process that name-matches a tool call marks its
// DESCENDANTS as belonging to that call by lineage — a structural claim,
// stronger than the name-floor comparison ever was. Precedence per node:
// its own verbatim match, then an ancestor's lineage, then its own weaker
// name match. `inherited` says which kind of claim a chip is making.
//
// Returns flattened render rows, tree order (roots by time, children under
// their parent): { type: "process"|"event", event, depth, match, attached }.
// `prune` drops process rows whose subtree holds no attached events — the
// scaffolding a file slice fetches for names but should not display bare.
export function buildSliceTree(events = [], candidates = [], { prune = false, clockTimes = null } = {}) {
  const procs = new Map(); // psk -> node
  const others = [];
  const candidatesFor = (event) => systemEventTurnCandidates(candidates, clockTimes, event?.ts);
  for (const e of events || []) {
    if (e?.kind === "ProcessStart") {
      const key = pskOf(e.data?.process_start_key) ?? pskOf(e.psk);
      const node = {
        key,
        event: e,
        parent: pskOf(e.data?.parent_process_start_key) ?? pskOf(e.data?.creator_process_start_key),
        children: [],
        attached: [],
        own: matchEventToTurn(e, candidatesFor(e)),
        match: null,
      };
      if (key && !procs.has(key)) procs.set(key, node);
      else others.push({ event: e, node });
    } else if (e) {
      others.push({ event: e });
    }
  }
  const roots = [];
  for (const node of procs.values()) {
    const parent = node.parent ? procs.get(node.parent) : null;
    if (parent && parent !== node) parent.children.push(node);
    else roots.push(node);
  }
  const unattributed = [];
  for (const o of others) {
    if (o.node) {
      // A duplicate-key ProcessStart (a retransmit) stays out of the tree but
      // not out of the reading — a dropped row is a claim nobody made.
      unattributed.push(o.event);
      continue;
    }
    const owner = pskOf(o.event.psk);
    const node = owner ? procs.get(owner) : null;
    if (node) node.attached.push(o.event);
    else unattributed.push(o.event);
  }

  const rows = [];
  const seen = new Set();
  const walk = (node, depth, inheritedFrom) => {
    if (seen.has(node)) return 0; // a cycle in the keys must not hang the dock
    seen.add(node);
    const ownExact = node.own && node.own.rank >= 3 ? node.own : null;
    const match = ownExact
      || (inheritedFrom != null ? { turn: inheritedFrom, inherited: true } : node.own);
    node.match = match || null;
    const downLineage = (ownExact || node.own)?.turn ?? inheritedFrom;
    const row = { type: "process", event: node.event, depth, match: node.match, attached: node.attached.length };
    rows.push(row);
    const at = rows.length;
    let kept = 0;
    for (const e of [...node.attached].sort((a, b) => a.ts - b.ts)) {
      rows.push({
        type: "event",
        event: e,
        depth: depth + 1,
        match: downLineage != null ? { turn: downLineage, inherited: true } : null,
      });
      kept += 1;
    }
    for (const child of [...node.children].sort((a, b) => a.event.ts - b.event.ts)) {
      kept += walk(child, depth + 1, downLineage ?? null);
    }
    if (prune && kept === 0) {
      rows.splice(at - 1, 1);
      return 0;
    }
    return kept + (prune ? 0 : 1);
  };
  for (const root of [...roots].sort((a, b) => a.event.ts - b.event.ts)) walk(root, 0, null);
  for (const e of [...unattributed].sort((a, b) => a.ts - b.ts)) {
    rows.push({ type: "event", event: e, depth: 0, match: matchEventToTurn(e, candidatesFor(e)) });
  }
  return rows;
}

// The best correspondence between one event and the slice's turns, or null.
// Strongest basis wins. Equal strongest matches from different turns are
// ambiguous and fail closed: repeated commands and paths must not create a
// confident but false owner chip.
export function matchEventToTurn(event, candidates = []) {
  const kind = event?.kind;
  const data = event?.data;
  if (!kind || !data) return null;
  let bestRank = -1;
  const bestByTurn = new Map();
  const consider = (turn, hit) => {
    if (!hit || hit.rank < bestRank) return;
    if (hit.rank > bestRank) {
      bestRank = hit.rank;
      bestByTurn.clear();
    }
    if (!bestByTurn.has(turn)) bestByTurn.set(turn, { turn, basis: hit.basis, rank: hit.rank });
  };
  const result = () => (bestByTurn.size === 1 ? [...bestByTurn.values()][0] : null);
  if (kind === "ProcessStart") {
    const line = data.command_line || data.image_name || "";
    const inner = unwrapHarnessCommand(line);
    for (const c of candidates) {
      for (const cand of c.commands) {
        consider(c.turn, matchCommand(line, cand));
        if (inner) consider(c.turn, matchCommand(inner, cand));
      }
    }
    return result();
  }
  if (kind === "FileState" || kind === "FileDelete" || kind === "FileRename") {
    const targets = [data.filepath, data.new_filepath, data.original_filepath].filter(Boolean);
    for (const c of candidates) {
      for (const target of targets) {
        for (const cand of c.paths) consider(c.turn, matchPath(target, cand));
      }
    }
    return result();
  }
  return null;
}
